import { CartPricing, HardwareDiscountPreview } from '../../types';
import { centsToDollarsNumber } from '../util/money';

export interface HardwareFirstEventProperties {
  hardware_first: true;
  tracker_count: number;
  promo_code?: string;
  /** whole percent the code takes off the hardware list price; 0 without a code */
  promo_percent: number;
  hardware_promo_discount: number;
  multi_device_discount: number;
}

export interface HardwareFirstProductProperties {
  list_price: number;
  discounted_price: number;
}

/**
 * The arm's pricing for the cart-level segment events, summed over the repriced hardware units.
 * Undefined for a control cart so spreading the result leaves that payload unchanged.
 */
export function hardwareFirstEventProperties(
  cartPricing: Pick<CartPricing, 'hardwareDiscounts'> | undefined,
  couponCode?: string,
): HardwareFirstEventProperties | undefined {
  const discounts = Object.values(cartPricing?.hardwareDiscounts ?? {});
  if (discounts.length === 0) {
    return undefined;
  }

  const listInCents = discounts.reduce((sum, discount) => sum + discount.listPriceInCents, 0);
  const promoInCents = discounts.reduce((sum, discount) => sum + discount.promoDiscountInCents, 0);
  const multiDeviceInCents = discounts.reduce((sum, discount) => sum + discount.multiDeviceDiscountInCents, 0);

  return {
    hardware_first: true,
    tracker_count: discounts.length,
    ...(couponCode && { promo_code: couponCode }),
    promo_percent: listInCents > 0 ? Math.round((promoInCents / listInCents) * 100) : 0,
    hardware_promo_discount: centsToDollarsNumber(promoInCents),
    multi_device_discount: centsToDollarsNumber(multiDeviceInCents),
  };
}

/** The list and repriced figures for one product row; undefined when the server did not reprice it. */
export function hardwareFirstProductProperties(
  discount: HardwareDiscountPreview | undefined,
): HardwareFirstProductProperties | undefined {
  if (!discount) {
    return undefined;
  }

  return {
    list_price: centsToDollarsNumber(discount.listPriceInCents),
    discounted_price: centsToDollarsNumber(discount.discountedPriceInCents),
  };
}
